import { ApolloLink } from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';

import { getExpectedCompanyId } from '../../lib/companySession';

/**
 * Apollo link for the dev-only bypass-token headers. The real session token now rides on
 * the HttpOnly `altitude_session` cookie that the backend sets on `/auth/callback` —
 * `credentials: include` on the HttpLink is what carries it.
 *
 * Why the bypass token is still here: integration tests and Cypress run with `auth.bypass`
 * on, which lets the backend short-circuit on a `Bearer bypass-<id>` header. That avoids
 * having to seed a real WorkOS session for tests.
 *
 * The session-only variant (`Bearer bypass-session-<sub>`) is used by the onboarding
 * specs that need a "WorkOS session, no Altitude row" state. For those, the SPA also
 * forwards the email / firstName / lastName from localStorage as `X-Test-Bypass-…`
 * headers so the backend can populate `workosVerified` without a DB lookup.
 */
const BYPASS_TOKEN_KEY = 'altitude:auth:bypass:token';
const BYPASS_EMAIL_KEY = 'altitude:auth:bypass:email';
const BYPASS_FIRST_NAME_KEY = 'altitude:auth:bypass:firstName';
const BYPASS_LAST_NAME_KEY = 'altitude:auth:bypass:lastName';

export const authLink = (): ApolloLink => {
  return new SetContextLink((prevContext) => {
    const headers = (prevContext.headers ?? {}) as Record<string, string>;
    const bypassToken = localStorage.getItem(BYPASS_TOKEN_KEY);
    const bypassIsSessionOnly = bypassToken?.startsWith('bypass-session-');

    const customHeaders: Record<string, string> = {};

    if (bypassIsSessionOnly) {
      const email = localStorage.getItem(BYPASS_EMAIL_KEY);
      const firstName = localStorage.getItem(BYPASS_FIRST_NAME_KEY);
      const lastName = localStorage.getItem(BYPASS_LAST_NAME_KEY);
      if (email) {
        customHeaders['x-test-bypass-email'] = email;
      }
      if (firstName) {
        customHeaders['x-test-bypass-first-name'] = firstName;
      }
      if (lastName) {
        customHeaders['x-test-bypass-last-name'] = lastName;
      }
    }

    // The company this page's data belongs to. The backend refuses the request when the session has moved elsewhere.
    const expectedCompanyId = getExpectedCompanyId();
    if (expectedCompanyId) {
      customHeaders['x-expected-company-id'] = expectedCompanyId;
    }

    return {
      headers: {
        ...headers,
        ...customHeaders,
        ...(bypassToken ? { authorization: `Bearer ${bypassToken}` } : {}),
      },
    };
  });
};
